import { useMemo, useCallback } from 'react';

import { MenuItem, Skeleton, Typography } from '@mui/material';

import { ContentSupportedChannels } from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import { CampaignChannels } from 'domains/campaigns/types';
import { Template, SMS } from 'domains/content/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import CreateNewDropDownItem from 'domains/core/components/CreateNewDropDownItem';
import { DirectMailListItem } from 'hooks/queries/useDirectMails';

import { AutocompleteOption } from '../Autocomplete/Autocomplete';

import ButtonContentPreview from './ButtonContentPreview';

export type ContentItem = Template | SMS | DirectMailListItem;

export type Props = {
    options: AutocompleteOption<ContentItem>[];
    value: AutocompleteOption<ContentItem> | null;
    onChange: (e: React.SyntheticEvent, option: AutocompleteOption<ContentItem> | null) => void;
    onInputChange: (e: React.SyntheticEvent, inputValue: string) => void;
    contentType?: ContentSupportedChannels;
    isLoading?: boolean;
    isPreviewVisible?: boolean;
    selectedContentId?: number | null;
    textfieldInput?: string;
    onContentPreviewClick?: () => void;
    onCreateNew?: () => void;
    // When true, shows "Unavailable" placeholder with error state (e.g., content was unpublished)
    isContentUnavailable?: boolean;
};

const AutocompleteContent = ({
    options = [],
    value,
    onChange,
    onInputChange,
    contentType = CampaignChannels.EMAIL,
    isLoading = false,
    isPreviewVisible = false,
    selectedContentId = null,
    textfieldInput = '',
    onContentPreviewClick,
    onCreateNew,
    isContentUnavailable = false,
}: Props) => {
    // Find selected option based on ID when not explicitly provided
    const selectedOption = useMemo(() => {
        if (value) return value;
        if (selectedContentId === null) return null;

        return options.find((option) => option.value?.id === selectedContentId && option.index !== 0) || null;
    }, [selectedContentId, options, value]);

    const getNoOptionsText = useCallback(() => {
        if (isLoading) return 'Loading...';
        if (textfieldInput && options.length <= 1) return 'No content match your search'; // Account for "Create New" option
        if (options.length <= 1) return `No content available`;
        return 'No options';
    }, [isLoading, textfieldInput, options.length]);

    return (
        <Autocomplete
            data-test="content-autocomplete"
            data-testid="content-autocomplete"
            freeSolo={false}
            fullWidth
            getOptionLabel={(option) => option.label ?? ''}
            inputValue={textfieldInput ?? ''}
            isOptionEqualToValue={(option, value) => {
                if (!option.value || !value.value) return false;
                return option.value.id === value.value.id;
            }}
            label="Content"
            leadingIcon={
                selectedOption && onContentPreviewClick ? (
                    <ButtonContentPreview
                        contentType={contentType}
                        onClick={onContentPreviewClick}
                        isActive={isPreviewVisible}
                    />
                ) : null
            }
            loading={isLoading}
            loadingText={<Skeleton height={36} />}
            noOptionsText={getNoOptionsText()}
            onChange={onChange}
            onInputChange={onInputChange}
            options={options}
            placeholder={isContentUnavailable ? 'Unavailable' : 'Select or create content'}
            renderOption={({ className, ...props }, { index, label, value }) => {
                const isCreateOption = index === 0;

                if (isCreateOption) {
                    return <CreateNewDropDownItem {...props} key={index} value={value} onClick={onCreateNew} />;
                }

                return (
                    <MenuItem {...props} key={value.id} value={value}>
                        <Typography>{label}</Typography>
                    </MenuItem>
                );
            }}
            type="string"
            value={selectedOption}
            clearOnEscape
            error={isContentUnavailable}
        />
    );
};

export default AutocompleteContent;
